import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { ExternalBoard, ExternalProject } from '../projects-client/projects-client.type';
import {
  ADOProject,
  ADOTeam,
  BoardJDC,
  ExternalService,
  Integration,
  IntegrationEntry,
  IntegrationJDC,
  IntegrationRefreshData,
  SetStatusMappingPayload,
  StartIntegrationProcessPayload,
} from './integrations-client.type';

const axiosInstance = AxiosInstance();
const baseURL = appConfig.VITE_APP_API_URL;

/**
 * Fetches all available integrations from the API
 *
 * @returns {Promise<Integration[]>} A promise that resolves to an array of Integration objects
 * @throws {Error} If the API request fails, rejects with the error response data
 */
const getIntegrations = (): Promise<Integration[]> => {
  const url = `${baseURL}/integrations/`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response.data));
};

/**
 * Creates a system access for an integration
 *
 * @param {string} id - The integration ID
 * @param {string} orgId - The organization ID
 * @param {string} token - The authentication token
 * @returns {Promise<{ id: string, message: string }>} A promise that resolves to the created system access data
 * @throws {Error} If the API request fails, rejects with the error response data
 */
const createSystemAccess = (id: string, orgId: string, token: string): Promise<{ id: string; message: string }> => {
  const url = `${baseURL}/integrations/${id}/system-accesses`;
  const postBody = {
    integration_id: id,
    organization_id: orgId,
    auth_token: token,
  };

  return axiosInstance
    .post(url, postBody)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response.data));
};

/**
 * Starts the integration process for a given payload
 *
 * @param {StartIntegrationProcessPayload} payload - The payload for starting the integration process
 * @returns {Promise<Record<string, unknown>>} A promise that resolves to the response data
 * @throws {Error} If the API request fails, rejects with the error response data
 */
const startIntegrationProcess = (payload: StartIntegrationProcessPayload): Promise<Record<string, unknown>> => {
  const url = `${baseURL}/integrations/processes`;

  return axiosInstance
    .post(url, payload)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response.data));
};

/**
 * Method to fetch the handshake url for getting the oauth token for a given service
 * @param service string service name
 * @returns the handshake url
 */
const getIntegrationUrl = (service: ExternalService): Promise<{ url: string }> => {
  const url = baseURL + `/integrations/get-auth-link?service=${service}`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
};

/**
 * Generates a status mapping for a given set of external (jira/ado) statuses
 * @param {string} organizationId - The organization ID
 * @param {string[]} externalStatuses - External statuses to map to categorize
 * @returns {Promise<{ [key: string]: string[] }>} A promise that resolves to an object with the status mapping
 */
function generateStatusMapping(
  organizationId: string,
  externalStatuses: string[],
): Promise<{ [key: string]: string[] }> {
  const url = baseURL + '/integrations/generate-status-mapping';
  const postBody = {
    statuses: externalStatuses,
    organization_id: organizationId,
  };
  return axiosInstance
    .post(url, postBody)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response }));
}

/**
 * Method to complete the oauth process for a given service
 *
 * @param {ExternalService} service - The service to complete the oauth process for
 * @param {string} userId - The user ID
 * @param {string} orgId - The organization ID
 * @param {string} code - The code
 * @param {string} state - The state
 * @param {string} integrationId - The integration ID
 * @param {Object} options - The options
 * @returns {Promise<Record<string, unknown>>} Promise of the response
 */
const completeOauth = (
  service: ExternalService,
  userId: string,
  orgId: string,
  code: string,
  state: string,
  integrationId: string | null,
  options: { [key: string]: string } = {},
): Promise<Record<string, unknown>> => {
  const url = baseURL + '/integrations/create-auth-token';
  const postBody = {
    userId,
    orgId,
    code,
    service,
    state,
    integrationId,
    ...options,
  };
  return axiosInstance
    .post(url, postBody)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response));
};

const setStatusMapping = (
  serviceName: ExternalService,
  project: ExternalProject | ADOProject | IntegrationJDC,
  board: ExternalBoard | ADOTeam | BoardJDC,
  orgId: string,
  backlogSections: string[],
  inProgressSection: string[],
  blockedSection: string[],
  inReviewSection: string[],
  inQASection: string[],
  readyForDeploymentSection: string[],
  doneSections: string[],
  projectUrl: string,
  configurationState?: string,
) => {
  const url = baseURL + '/integrations/save-status-mapping';
  const postBody: SetStatusMappingPayload = {
    service_name: serviceName,
    project: project,
    board: board,
    org_id: orgId,
    backlog_section: backlogSections,
    blocked_section: blockedSection,
    in_progress_section: inProgressSection,
    in_review_section: inReviewSection,
    in_test_section: inQASection,
    ready_for_deployment_section: readyForDeploymentSection,
    done_section: doneSections,
    project_url: projectUrl,
    configuration_state: configurationState,
  };
  if (serviceName === ExternalService.JDC) {
    postBody['service_name'] = 'jira_data_center';
  }
  if (!projectUrl) {
    delete postBody['project_url'];
  }
  return axiosInstance
    .post(url, postBody)
    .then((response) => response.data)
    .catch((error) => Promise.reject({ error: error.response.data }));
};

/**
 * Retrieves the integration entries from the backend API.
 *
 * @param {string} organizationId - The ID of the organization to retrieve integration entries for
 * @returns {Promise<IntegrationEntry[]>} A promise that resolves to an array of IntegrationEntry objects.
 */
const getIntegrationEntries = (organizationId: string): Promise<IntegrationEntry[]> => {
  const url = `${baseURL}/api/system-accesses/?organization_id=${organizationId}`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response.data));
};

/**
 * Refreshes the integration by making a request to the specified integration ID's data refresh endpoint.
 *
 * @param {string} integrationId - The ID of the integration to be refreshed
 * @returns {Promise<IntegrationRefreshData>} A promise that resolves to an object containing refreshed data and status
 */
const refreshIntegration = (integrationId: string): Promise<IntegrationRefreshData> => {
  const url = `${baseURL}/api/system-accesses/${integrationId}/request-data-refresh/`;
  return axiosInstance
    .get(url)
    .then((response) => ({ ...response.data, status: response.status }))
    .catch((error) => Promise.reject(error.response.data));
};

/**
 * Retrieves Jira Data Center integrations.
 *
 * @param {string} organizationId - The ID of the organization to fetch integrations for.
 * @returns {Promise<IntegrationJDC[]>} A promise that resolves with an array of IntegrationJDC objects.
 */
const getIntegrationsJDC = (organizationId: string): Promise<IntegrationJDC[]> => {
  const url = `${baseURL}/integrations/jdc/get-integrations?organization_id=${organizationId}`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response.data));
};

/**
 * Retrieves the board statuses from JDC for a specific board.
 *
 * @param {string} boardId - The ID of the board.
 * @returns {Promise<{ statuses: string[] }>} The board statuses.
 */
const getBoardStatusesJDC = (boardId: string): Promise<{ statuses: string[] }> => {
  const url = `${baseURL}/integrations/jdc/get-board-statuses?board_id=${boardId}`;
  return axiosInstance
    .get(url)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error.response.data));
};

/**
 * Configures JDC integration for a specific board.
 *
 * @param {string} boardId - The ID of the board.
 * @param {number} devTeamSize - The size of the development team.
 * @param {boolean} pointBugs - Whether to assign points to bug cards.
 * @param {boolean} requireLabels - Whether labels are mandatory for cards.
 * @param {string} workingHoursStart - The start time for working hours.
 * @param {string} workingHoursEnd - The end time for working hours.
 * @return {Promise<number>} A promise that resolves with the response status or rejects with an error.
 */
const configureJDC = (
  boardId: string,
  devTeamSize: number,
  pointBugs: boolean,
  requireLabels: boolean,
  workingHoursStart: string,
  workingHoursEnd: string,
  configurationState: string,
): Promise<number> => {
  const url = `${baseURL}/integrations/jdc/configure`;
  const postBody = {
    board_id: boardId,
    dev_team_size: devTeamSize,
    points_for_bug_cards: pointBugs,
    labels_for_cards_mandatory: requireLabels,
    working_hours_start: workingHoursStart,
    working_hours_end: workingHoursEnd,
    configuration_state: configurationState,
  };
  return axiosInstance
    .post(url, postBody)
    .then((response) => response.status)
    .catch((error) => Promise.reject({ error: error.response.data }));
};

export {
  completeOauth,
  configureJDC,
  createSystemAccess,
  generateStatusMapping,
  getBoardStatusesJDC,
  getIntegrationEntries,
  getIntegrations,
  getIntegrationsJDC,
  getIntegrationUrl,
  refreshIntegration,
  setStatusMapping,
  startIntegrationProcess,
};
